import { colors } from '@hazadapt-git/public-core-base'
import Help from '@mui/icons-material/Help'
import IconButton from '@mui/material/IconButton'
import MuiPopover from '@mui/material/Popover'
import { PopoverOrigin } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import {
    FC,
    useState,
    MouseEvent,
    CSSProperties,
    MouseEventHandler,
    ReactNode,
} from 'react'

import { theme, useStyles } from '../../lib/styles/universal'
import { Markdown } from './Markdown'

interface PopoverProps {
    helpText: string | ReactNode
    popoverPlacement?: 'left' | 'right' | 'center'
    popoverPaperStyle?: CSSProperties
    popoverTextStyle?: CSSProperties
    onClick?: MouseEventHandler<HTMLDivElement>
    icon?: ReactNode
    color?: string
    anchorOrigin?: PopoverOrigin
    transformOrigin?: PopoverOrigin
}

export const Popover: FC<PopoverProps> = ({
    helpText,
    popoverPlacement = 'center',
    popoverPaperStyle = {},
    popoverTextStyle = {},
    onClick,
    icon = <Help />,
    color = colors.grays.NIMBUS,
    anchorOrigin = {
        vertical: 'top',
        horizontal: 'center',
    },
    transformOrigin = {
        vertical: 'bottom',
        horizontal:
            popoverPlacement === 'left'
                ? 'right'
                : popoverPlacement === 'right'
                ? 'left'
                : 'center',
    },
}: PopoverProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
    }
    const handleClose = () => setAnchorEl(null)
    const open = Boolean(anchorEl)
    const { classes } = useStyles()

    const useLocalStyles = makeStyles()({
        container: {
            ...popoverTextStyle,
            cursor: 'default',
            margin: '1rem',
            width: 'fit-content',
            maxWidth: '80vw',
            [theme.breakpoints.up('sm')]: {
                maxWidth: '30rem',
            },
        },
    })
    const { classes: localClasses } = useLocalStyles()

    return (
        <div className={classes.helpIcon} onClick={onClick}>
            <IconButton
                sx={{ color }}
                onClick={handleClick}
                aria-label="More information"
            >
                {icon}
            </IconButton>
            <MuiPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        style: {
                            ...popoverPaperStyle,
                            backgroundColor: colors.grays.WHITESMOKE,
                            borderRadius: '1rem',
                        },
                    },
                }}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
            >
                {typeof helpText === 'string' ? (
                    <Markdown
                        containerClassName={localClasses.container}
                        content={helpText}
                    />
                ) : (
                    <div className={localClasses.container}>{helpText}</div>
                )}
            </MuiPopover>
        </div>
    )
}
